import { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, AlertCircle, Ban, BookOpen } from 'lucide-react';
import Sidebar from '../components/Sidebar';
import VoidConfirmationModal from '../components/VoidConfirmationModal';
import {
  getInventoryAdjustmentById,
  voidInventoryAdjustment,
} from '../utils/inventoryAdjustmentApi';
import { ApiValidationError } from '../types/auth';
import type { InventoryAdjustment } from '../types/inventoryAdjustment';
import { formatCurrency, formatPreviewQuantity } from '../utils/formatCurrency';
import { toast } from '../utils/toast';
import { adjustmentVoidCopy } from '../utils/voidConfirmationCopy';
import {
  canVoidAdjustment,
  formatAdjustmentDate,
  formatAdjustmentLabel,
  formatAdjustmentTime,
  getAdjustmentStatusColor,
} from '../utils/inventoryAdjustmentForm';

const InventoryAdjustmentView = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const adjustmentId = Number(id);

  const [detail, setDetail] = useState<InventoryAdjustment | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [showVoidModal, setShowVoidModal] = useState(false);
  const [voiding, setVoiding] = useState(false);
  const [voidError, setVoidError] = useState<string | null>(null);

  const fetchDetail = async () => {
    if (!id || Number.isNaN(adjustmentId)) {
      setError('Invalid adjustment ID');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setDetail(await getInventoryAdjustmentById(adjustmentId));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load adjustment');
      setDetail(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDetail();
  }, [id]);

  const handleViewJournal = () => {
    if (detail?.transaction_id == null) return;
    navigate('/accounts', {
      state: {
        activeTab: 'journal',
        referenceTransactionId: detail.transaction_id,
      },
    });
  };

  const openVoidModal = () => {
    setVoidError(null);
    setShowVoidModal(true);
  };

  const cancelVoidModal = () => {
    setShowVoidModal(false);
    setVoidError(null);
  };

  const confirmVoid = async (reason: string) => {
    if (!canVoidAdjustment() || !detail) return;

    try {
      setVoiding(true);
      setVoidError(null);
      const voided = await voidInventoryAdjustment(detail.id, {
        void_reason: reason || null,
      });
      setShowVoidModal(false);
      setDetail(voided);
      toast.success(`${voided.adjustment_number} voided.`);
    } catch (err) {
      if (err instanceof ApiValidationError) {
        setVoidError(err.message);
      } else {
        toast.error(err instanceof Error ? err.message : 'Failed to void adjustment');
      }
    } finally {
      setVoiding(false);
    }
  };

  const canVoidDetail =
    detail != null && detail.status !== 'VOIDED' && canVoidAdjustment();

  const totalValue =
    detail?.lines.reduce((sum, line) => sum + line.value_change, 0) ?? 0;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex">
        <Sidebar />
        <main className="flex-1 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-800" />
        </main>
      </div>
    );
  }

  if (error || !detail) {
    return (
      <div className="min-h-screen bg-gray-100 flex">
        <Sidebar />
        <main className="flex-1 p-4 md:p-6">
          <div className="max-w-2xl mx-auto bg-white rounded-lg shadow p-6 text-center">
            <AlertCircle className="mx-auto h-10 w-10 text-red-500 mb-3" />
            <p className="text-gray-900 font-medium mb-2">Could not load adjustment</p>
            <p className="text-sm text-gray-500 mb-4">{error || 'Adjustment not found.'}</p>
            <div className="flex justify-center gap-3">
              <button
                type="button"
                onClick={fetchDetail}
                className="px-4 py-2 bg-slate-800 text-white rounded-lg text-sm hover:bg-slate-700"
              >
                Retry
              </button>
              <Link
                to="/inventory-adjustments"
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50"
              >
                Back to Adjustments
              </Link>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 flex">
      <Sidebar />
      <main className="flex-1 p-4 md:p-6 overflow-hidden">
        <div className="w-full max-w-full">
          <div className="mb-6">
            <Link
              to="/inventory-adjustments"
              className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900 mb-4"
            >
              <ArrowLeft className="h-4 w-4 mr-1" />
              Back to Inventory Adjustments
            </Link>
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4">
              <div>
                <h1 className="text-xl md:text-2xl font-semibold text-gray-900 font-mono">
                  {detail.adjustment_number}
                </h1>
                <div className="flex gap-2 mt-2">
                  <span
                    className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full ${getAdjustmentStatusColor(detail.status ?? 'OPEN')}`}
                  >
                    {formatAdjustmentLabel(detail.status ?? 'OPEN')}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="bg-gray-50 p-4 rounded-lg space-y-2 text-sm mb-6 max-w-xl">
              <h2 className="font-semibold text-gray-900 mb-3">Adjustment details</h2>
              <div className="flex justify-between">
                <span className="text-gray-600">Date</span>
                <span>{formatAdjustmentDate(detail.adjustment_date)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Account</span>
                <span className="font-medium">{detail.adjustment_account.name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Memo</span>
                <span className="text-right max-w-[60%]">{detail.memo || '—'}</span>
              </div>
              <div className="flex justify-between border-t pt-2">
                <span className="text-gray-600">Created</span>
                <span>
                  {formatAdjustmentDate(detail.created_at)}{' '}
                  {formatAdjustmentTime(detail.created_at)}
                </span>
              </div>
              {detail.status === 'VOIDED' && (
                <>
                  <div className="flex justify-between border-t pt-2">
                    <span className="text-gray-600">Voided at</span>
                    <span>
                      {detail.voided_at
                        ? `${formatAdjustmentDate(detail.voided_at)} ${formatAdjustmentTime(detail.voided_at)}`
                        : '—'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Voided by</span>
                    <span>{detail.voided_by?.name ?? '—'}</span>
                  </div>
                  {detail.void_reason && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Void reason</span>
                      <span className="text-right max-w-[60%]">{detail.void_reason}</span>
                    </div>
                  )}
                </>
              )}
            </div>

            <div className="mb-6 overflow-x-auto">
              <h2 className="font-semibold text-gray-900 mb-3">Line items</h2>
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                      Item
                    </th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                      Before
                    </th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                      Change
                    </th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                      After
                    </th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                      Value
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {detail.lines.map((line) => (
                    <tr key={line.id}>
                      <td className="px-4 py-2">
                        <div className="font-medium">{line.item.name}</div>
                        <div className="text-xs text-gray-500 font-mono">{line.item.sku}</div>
                        <div className="text-xs text-gray-400">
                          {formatAdjustmentLabel(line.movement_type)}
                        </div>
                      </td>
                      <td className="px-4 py-2 text-right font-mono">
                        {formatPreviewQuantity(line.qty_before)}
                      </td>
                      <td
                        className={`px-4 py-2 text-right font-mono ${
                          line.change_in_qty >= 0 ? 'text-green-600' : 'text-red-600'
                        }`}
                      >
                        {line.change_in_qty >= 0 ? '+' : ''}
                        {formatPreviewQuantity(line.change_in_qty)}
                      </td>
                      <td className="px-4 py-2 text-right font-mono">
                        {formatPreviewQuantity(line.qty_after)}
                      </td>
                      <td
                        className={`px-4 py-2 text-right font-mono ${
                          line.value_change >= 0 ? 'text-green-600' : 'text-red-600'
                        }`}
                      >
                        {formatCurrency(line.value_change)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex justify-end mb-6">
              <div className="w-full sm:w-80 bg-gray-50 p-4 rounded-lg text-sm">
                <div className="flex justify-between font-semibold">
                  <span>Total value change</span>
                  <span
                    className={`font-mono ${
                      totalValue >= 0 ? 'text-green-600' : 'text-red-600'
                    }`}
                  >
                    {formatCurrency(totalValue)}
                  </span>
                </div>
              </div>
            </div>

            <div className="flex flex-wrap justify-end gap-2 pt-4 border-t">
              {detail.transaction_id != null && (
                <button
                  type="button"
                  onClick={handleViewJournal}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm text-violet-700 border border-violet-200 rounded-lg hover:bg-violet-50"
                >
                  <BookOpen className="h-4 w-4" />
                  View journal
                </button>
              )}
              {canVoidDetail && (
                <button
                  type="button"
                  onClick={openVoidModal}
                  disabled={voiding}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm text-red-700 border border-red-200 rounded-lg hover:bg-red-50"
                >
                  <Ban className="h-4 w-4" />
                  Void
                </button>
              )}
            </div>
          </div>
        </div>

        {detail && (
          <VoidConfirmationModal
            isOpen={showVoidModal}
            {...adjustmentVoidCopy(detail.adjustment_number)}
            confirmText="Void adjustment"
            showReasonField
            isSubmitting={voiding}
            error={voidError}
            onCancel={cancelVoidModal}
            onConfirm={confirmVoid}
          />
        )}
      </main>
    </div>
  );
};

export default InventoryAdjustmentView;
